import React, { useEffect, useState } from 'react';
import useDetectScroll from '@smakss/react-scroll-direction';
import { useDebounce } from 'usehooks-ts';

type Props = {
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  topStarts: number; // 0 ~ 이 숫자 만큼 top이라고 취급
  hideStarts: number; // 숨김 처리할 높이
};
const useIsScrollDown = ({ scrollRef, hideStarts, topStarts }: Props) => {
  const [isScrollDown, setIsScrollDown] = useState(false);

  const [scrollTarget, setScrollTarget] = useState<HTMLDivElement>();

  const { scrollDir, scrollPosition } = useDetectScroll({
    target: scrollTarget,
  });

  // debounce 추가 - scrollPosition.top의 일시적인 변화 무시 (150ms)
  const debouncedScrollTop = useDebounce(scrollPosition.top, 150);

  useEffect(() => {
    if (scrollRef?.current) setScrollTarget(scrollRef.current);
  }, [scrollRef]);

  useEffect(() => {
    const top = Math.max(0, scrollPosition.top);

    // 1순위: 맨 위에 있으면 즉시 Header 펼치기 (ScrollToTop 버튼 대응)
    if (top <= topStarts) {
      if (isScrollDown) {
        setIsScrollDown(false);
      }
      return;
    }

    // 스크롤 끝 근처에서는 상태 변경 방지 (overscroll 효과로 인한 흔들림 방지)
    // 단, 아래로 스크롤해서 Header를 숨기는 경우는 예외 처리
    if (scrollTarget && scrollDir !== 'down') {
      const { scrollHeight, clientHeight } = scrollTarget;
      const scrollBottom = scrollHeight - (top + clientHeight);

      // 스크롤이 끝에서 50px 이내에 있으면 상태 변경 방지
      if (scrollBottom < 50) {
        return;
      }
    }

    // 아래로 스크롤 중 + 어느 정도 내려왔을 때만 숨김
    if (scrollDir === 'down' && top > hideStarts) {
      if (!isScrollDown) {
        setIsScrollDown(true);
      }
      return;
    }

    // 위로 스크롤 중이면 즉시 다시 보이게
    if (scrollDir === 'up') {
      if (isScrollDown) {
        setIsScrollDown(false);
      }
      return;
    }
  }, [
    scrollDir,
    scrollPosition.top,
    debouncedScrollTop,
    hideStarts,
    topStarts,
    scrollTarget,
    isScrollDown,
  ]);

  return isScrollDown;
};

export default useIsScrollDown;
